import './ProvidersList.scss';

import * as React from 'react';
import moment from 'moment-timezone';

import { Dispatch, bindActionCreators } from 'redux';
import { Break, ProviderSchedules } from '../../redux/providers/types';
import { HoverCard, HoverCardType, Link, Persona, PersonaSize, Toggle } from '@fluentui/react';
import { setProviderOnCall, setProviderOffCall } from '../../redux/providers/actions';

import { AsyncData } from '../../redux/core';
import { ROUTES } from '../../config/routes';
import { RootState } from '../../redux/types';
import { connect } from 'react-redux';
import { css } from '../../util/Css';
import { getZeroTimeDate } from '../../util/Date';
import { Market } from '../../api/markets.types';

export interface OwnProps {
  market: string;
}

interface StateProps {
  date: string;
  providerSchedules: AsyncData<ProviderSchedules[]>;
  markets: AsyncData<Market[]>;
}

interface DispatchProps {
  actions: {
    setProviderOnCall: (providerId: string, marketCode: string, name: string) => void;
    setProviderOffCall: (providerId: string, marketCode: string, name: string) => void;
  };
}

export type ProvidersListProps = OwnProps & StateProps & DispatchProps;

export interface ProvidersListState {
  isUpdating: boolean;
}

export class Component extends React.Component<ProvidersListProps, ProvidersListState> {
  constructor(props: ProvidersListProps) {
    super(props);
    this.state = {
      isUpdating: false,
    };
  }

  public render(): JSX.Element | null {
    const { providerSchedules, date } = this.props;
    if (!this.state.isUpdating && (!providerSchedules || !providerSchedules.data || providerSchedules.data.length === 0)) {
      return null;
    }

    const time = getZeroTimeDate(date).getTime();
    if (time === getZeroTimeDate().getTime()) {
      return (
        <div className="providers-list-container">
          {this.renderProviders(true)}
          {this.renderProviders(false)}
        </div>
      );
    }

    return (
      <div className="providers-list-container">
        <div className="providers-list on-call">{this.props.providerSchedules?.data?.map((i) => this.renderProvider(i, false))}</div>
      </div>
    );
  }
  public componentDidUpdate(prevProps: ProvidersListProps) {
    if (this.props.providerSchedules.isFetching !== prevProps.providerSchedules.isFetching) {
      this.setState({ isUpdating: this.props.providerSchedules.isFetching });
    }
  }

  private renderProviders(onCall: boolean): JSX.Element {
    const toRender = (this.props.providerSchedules?.data || []).filter((p) => (p.provider.availability === 'ON_CALL') === onCall);

    return (
      <div className={css('providers-list', { 'on-call': onCall, 'off-call': !onCall })}>
        <div style={{ marginRight: 6 }}>{onCall ? 'ON CALL' : 'OFF CALL'}</div>
        {toRender.map((i) => this.renderProvider(i))}
      </div>
    );
  }

  private renderProvider(providerSchedule: ProviderSchedules, showToggle = true): JSX.Element {
    const name = `${providerSchedule.provider.firstName} ${providerSchedule.provider.lastName}`;
    const plainCardProps = {
      onRenderPlainCard: (props: any) => {
        return (
          <div className="persona-hover-card">
            <div className="flex flex-row justify-space-between">
              <div style={{ marginRight: 20 }}>
                <Link href={`${ROUTES.providers}/${props.provider.providerId}`}>{props.provider.fullName}</Link>
                {props.schedules.map((s: any, i: number) => (
                  <div key={i}>
                    {s.timeFormatted} [{s.positionType}]
                  </div>
                ))}
                <div>{props.provider.phone}</div>
              </div>
              {showToggle && (
                <Toggle
                  disabled={this.state.isUpdating}
                  checked={providerSchedule.provider.availability === 'ON_CALL'}
                  onText="On call"
                  offText="Off call"
                  onChange={(ev, value) => this.changeOnCallStatus(providerSchedule.provider.id, value!, name)}
                />
              )}
            </div>
            <div>{props.provider.email}</div>
            {props.breaks.length > 0 && (
              <>
                <div>Breaks:</div>
                <div className="breaks">{props.breaks.map((b: Break) => this.renderBreak(b))}</div>
              </>
            )}
          </div>
        );
      },
      renderData: providerSchedule,
    };
    return (
      <HoverCard key={providerSchedule.provider.id} plainCardProps={plainCardProps} instantOpenOnClick={true} type={HoverCardType.plain}>
        <Persona className="persona-subdued-background" hidePersonaDetails={true} size={PersonaSize.size32} text={name} imageUrl={providerSchedule.provider.avatarUrl} />
      </HoverCard>
    );
  }

  private changeOnCallStatus(providerId: string, checked: boolean, name: string) {
    if (checked) {
      this.props.actions.setProviderOnCall(providerId, this.props.market, name);
    } else {
      this.props.actions.setProviderOffCall(providerId, this.props.market, name);
    }
  }

  private getMarketTimeZone(marketCode: string): string {
    const market = this.props.markets.data && this.props.markets.data.find((m) => m.code === marketCode);
    return market ? market.timeZone : '';
  }

  private renderBreak(singleBreak: Break): JSX.Element {
    const marketTimeZone = this.getMarketTimeZone(this.props.market)!;
    const format = moment.tz.guess() === marketTimeZone ? 'h:mm A' : 'h:mm A z';
    return (
      <div key={singleBreak.id}>
        From {moment(singleBreak.startTime).tz(marketTimeZone).format(format)} for {(singleBreak.endTime - singleBreak.startTime) / 60000} mins
      </div>
    );
  }
}

export function mapStateToProps(state: RootState, ownProps: OwnProps): StateProps {
  return {
    date: state.ui.date,
    providerSchedules: state.providers.marketToProviderSchedules[ownProps.market] || {},
    markets: state.markets.markets,
  };
}

export function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    actions: bindActionCreators({ setProviderOnCall, setProviderOffCall }, dispatch),
  };
}

export const ProvidersList = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component);
